import { EntityId } from 'entities-next/common'
import { Stage } from 'entities-next/stages'
import { isNil, omit } from 'lodash'
import { Deal, DealEmail, DealInfoData, DealTransition, IdIndexedDeals } from 'scenes/Dashboard/data/deals/types'
import { ApiCaller, callApi, getUrlWithPathParams } from 'services/api/utils'

import { CorporateStatusFilter } from '../../scenes/DealManager/duck/types'
import { Analysis } from '../analyses/analyses.types'

const ADMIN_DEALS_ENDPOINT_PATH = '/admin/deals/'

type ApiDeal = {
  readonly id: number
  readonly organizationId: number
  readonly trackOrganization: boolean
  readonly referrerId: number
  readonly currentFunnelStageId: EntityId
  readonly currentStageReason?: Deal['currentStageReason']
  readonly receivedOn: string
  readonly notes?: string
  readonly isCalibration: boolean
  readonly analysis: Analysis
}

function mapDealToApiData(deal: Partial<Deal>) {
  const { receivedOn, ...otherDealData } = deal
  return {
    ...otherDealData,
    receivedOn: receivedOn ? new Date(receivedOn).toISOString() : '',
  }
}

function parseApiDataToDeal(deal: ApiDeal) {
  const { receivedOn, ...otherDealData } = deal
  return {
    ...otherDealData,
    receivedOn: receivedOn ? receivedOn.split('T')[0] : '',
  } as Deal
}

export async function apiUpdateDealFinderStage(dealId: Deal['id'], newStage: number | string): Promise<Deal> {
  const data = await callApi<ApiDeal>({
    method: 'patch',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, dealId),
    data: {
      passed: false,
      currentFinderStage: newStage,
      currentFunnelStageId: null,
    },
  })
  return parseApiDataToDeal(data)
}

export async function apiUpdateDealSearchFunctionInfo(
  dealId: Deal['id'],
  dealData: Pick<Deal, 'searchFunctionId' | 'fitsWithRelaxedCriteria'>
): Promise<Deal> {
  const data = await callApi<ApiDeal>({
    method: 'patch',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, dealId),
    data: dealData,
  })
  return parseApiDataToDeal(data)
}

export async function apiDemoteDeal(id: number): Promise<Deal> {
  const params = {
    currentFunnelStageId: null,
    currentFinderStage: null,
  }
  const data = await callApi<ApiDeal>({
    method: 'patch',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, id),
    data: params,
  })
  return parseApiDataToDeal(data)
}

export async function apiMarkDealAsNew(id: number): Promise<Deal> {
  const data: Pick<Deal, 'lastInteractionAt'> = { lastInteractionAt: null }
  const response = await callApi<ApiDeal>({
    method: 'patch',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, id),
    data,
  })
  return parseApiDataToDeal(response)
}

export async function apiUpdateDealInfo(id: number, dealInfo: DealInfoData): Promise<Deal> {
  const data = await callApi<ApiDeal>({
    method: 'patch',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, id),
    data: mapDealToApiData(dealInfo),
  })
  return parseApiDataToDeal(data)
}

export async function apiUpdateDealData(id: number, { searchFunctionId, ...otherData }: Partial<Deal>): Promise<Deal> {
  const data = await callApi<ApiDeal>({
    method: 'patch',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, id),
    data: { ...otherData, searchFunctionId: !isNil(searchFunctionId) ? searchFunctionId : null },
  })
  return parseApiDataToDeal(data)
}

export async function apiDeleteDeal(dealId: ApiDeal['id']): Promise<unknown> {
  return callApi({
    method: 'delete',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, dealId),
  })
}

type GetManagerDealsFilters = {
  finderStagesFilter: Stage['id'][]
  organizationSearch?: string
  corporateSearch?: string
  corporateStatusFilter?: CorporateStatusFilter
  scoutingCampaignId?: string
  isValid: string
  processedByOps?: boolean
}

export async function apiGetManagerDeals(
  currentPage: number = 0,
  limit: number = 0,
  {
    finderStagesFilter,
    organizationSearch,
    corporateSearch,
    scoutingCampaignId,
    corporateStatusFilter,
    isValid,
    processedByOps,
  }: GetManagerDealsFilters
): Promise<IdIndexedDeals> {
  if (corporateStatusFilter === CorporateStatusFilter.all) {
    corporateStatusFilter = undefined
  }

  const dealsData = await callApi<ApiDeal[]>({
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH),
    params: {
      skip: currentPage * limit,
      limit,
      organizationSearch,
      corporateSearch,
      corporateStatus: corporateStatusFilter,
      scoutingCampaignId: scoutingCampaignId,
      finderStages: finderStagesFilter,
      isValid,
      processedByOps,
    },
  })
  return (dealsData || []).map(parseApiDataToDeal)
}

const apiGetDealFactory = (apiCaller: ApiCaller) => async (id: number): Promise<Deal> => {
  const dealData = await apiCaller<ApiDeal>({
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, id),
  })
  return parseApiDataToDeal(dealData)
}

export const apiGetDeal = apiGetDealFactory(callApi)
export const apiGetDealAsAdmin = apiGetDealFactory(callApi)

export async function apiSendEmail(data: DealEmail): Promise<void> {
  const body = omit(data, 'dealId')

  return await callApi<void>({
    method: 'post',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, data.dealId, '/email'),
    data: body,
  })
}

export async function apiSendDealToInvestor(id: number): Promise<void> {
  const data = {
    transition: DealTransition.SEND_TO_INVESTOR,
  }
  return await callApi<void>({
    method: 'post',
    url: getUrlWithPathParams(ADMIN_DEALS_ENDPOINT_PATH, id, '/transition'),
    data,
  })
}
